/**
 * PersonaCard — read-only detail view of a saved Persona.
 * Shows icon, name, genre, source badge, and preview of stored architectures.
 */

import React from 'react';
import { Badge } from '@components/ui';
import { Trash2 } from 'lucide-react';
import type { Persona } from '@/types/persona.types';

interface PersonaCardProps {
  persona: Persona;
  isActive?: boolean;
  compact?: boolean;
  onDelete?: (id: string) => void;
}

const SOURCE_LABELS: Record<string, string> = {
  crossroads: 'Crossroads',
  'band-persona-architect': 'Band Persona',
  manual: 'Manual',
};

export const PersonaCard: React.FC<PersonaCardProps> = ({ persona, isActive, compact, onDelete }) => {
  const sourceLabel = SOURCE_LABELS[persona.identitySource] || persona.identitySource;
  const hasVocal = !!persona.vocalArchitecture;
  const hasProduction = !!persona.productionArchitecture;
  const hasBand = !!persona.bandIdentityBlock;

  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
        isActive ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800 bg-slate-900/50'
      }`}>
        <span className="text-xl">{persona.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-200 truncate">{persona.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {persona.genre && <span className="text-[10px] text-slate-500">{persona.genre}</span>}
            <Badge variant="default-muted">{sourceLabel}</Badge>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl border transition-all ${
      isActive ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800 bg-slate-900/50'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{persona.icon}</span>
          <div>
            <p className="text-sm font-bold text-slate-200">{persona.name}</p>
            {persona.description && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{persona.description}</p>}
          </div>
        </div>
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(persona.id); }}
            className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Delete persona"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {persona.genre && <Badge variant="primary">{persona.genre}</Badge>}
        <Badge variant="default-muted">{sourceLabel}</Badge>
        {hasVocal && <Badge variant="info">Vocal</Badge>}
        {hasProduction && <Badge variant="info">Production</Badge>}
        {hasBand && <Badge variant="info">Identity</Badge>}
      </div>

      {persona.tags && (
        <div className="flex flex-wrap gap-1 mt-2">
          {persona.tags.split(',').map(t => t.trim()).filter(Boolean).map(tag => (
            <span key={tag} className="px-1.5 py-0.5 bg-slate-800 text-[10px] text-slate-500 rounded">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
};
